'use client';

import { forwardRef, useCallback, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { BannerRootContext } from './context/banner-root.context';
import { bannerRecipe } from './styles/banner-root.styles';
import type { BannerRootPropsInterface } from './types/banner-root.types';

/**
 * @since 10.0.0
 */

export const BannerRoot = forwardRef<HTMLDivElement, BannerRootPropsInterface>(
  (props, forwardedRef) => {
    const {
      tone = 'info',
      children,
      className,
      css: cssProp,
      isOpen,
      onClose,
      role = 'status',
      ...rest
    } = props;

    const [openState, setOpenState] = useControllableValue<boolean | undefined>({
      controlledValue: isOpen,
      uncontrolledValue: true,
      isControlled: 'isOpen' in props,
      isUncontrolled: false,
      componentName: 'Banner.Root',
      controlledPropName: 'isOpen',
    });

    const closeBanner = useCallback(() => {
      setOpenState(false);
    }, [setOpenState]);

    const contextValue = useMemo(
      () => ({
        tone,
        onClose,
        isOpen: openState,
        closeBanner,
      }),
      [tone, onClose, openState, closeBanner],
    );

    if (!openState) {
      return null;
    }

    const styles = css(bannerRecipe.raw({ tone }), cssProp);

    return (
      <BannerRootContext.Provider value={contextValue}>
        <div
          ref={forwardedRef}
          data-c11n-component="Banner.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_banner__root', styles, className)}
          role={role}
          {...rest}
        >
          {children}
        </div>
      </BannerRootContext.Provider>
    );
  },
);

BannerRoot.displayName = 'Banner.Root';
